// src/components/HeaderComponent.js
import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Nav, Container, Button, Dropdown, Offcanvas } from 'react-bootstrap';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePWA } from '../context/PWAContext'; // ✅ IMPORT PWA CONTEXT
import NotificationBell from './NotificationBell';

import { 
  Menu, UserCircle, LayoutGrid, Briefcase, Users, CreditCard, 
  Clock, Star, PlayCircle, Settings, LogOut, Home, Workflow,
  ChartBarDecreasingIcon, LayoutDashboard, ChevronRight, ChevronLeft,
  Phone, LogIn, UserPlus, Download,MessageSquare // ✅ Added Download icon
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import './css/HeaderComponent.css';
import logoBlackWhite from '../assets/img/logo_main_header.png';

const HeaderComponent = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); 
  const { isAuthenticated, user, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const notificationCount = notifications.length;
  const navRef = useRef(null);

  // ✅ PWA STATE
  const { isStandalone, isDismissed, promptInstall } = usePWA();
  // Only show the header install button if the popup was dismissed AND the app is not installed
  const showInstallHeaderBtn = isDismissed && !isStandalone;

  const isActive = (path) => {
    return location.pathname === path || location.pathname.includes(path);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClose = () => setShowDrawer(false);
  const handleShow = () => setShowDrawer(true);

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const scrollLeft = () => { if (navRef.current) navRef.current.scrollLeft -= 100; };
  const scrollRight = () => { if (navRef.current) navRef.current.scrollLeft += 100; };

  const renderDashboardLinks = (isMobile = false) => {
    const roleCode = String(user?.roleCode);
    const clickHandler = isMobile ? handleClose : undefined;

    const TalentLinks = (
      <>  
        <Nav.Link as={Link} to="/" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('recommended') ? 'active-tab-box' : ''}`}><Home size={18} /> Home</Nav.Link>
        <Nav.Link as={Link} to="/roles" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('ongoing') ? 'active-link-box' : ''}`}><PlayCircle size={18} /> Browse Roles</Nav.Link>
        <Nav.Link as={Link} to="/how-it-works" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('how-it-works') ? 'active-tab-box' : ''}`}><Workflow size={18} /> How It Works</Nav.Link>
        <Nav.Link as={Link} to="/dashboard/overview" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('overview') ? 'active-tab-box' : ''}`}><LayoutDashboard size={18} /> Dashboard</Nav.Link>
      </>
    );

    const DirectorLinks = (
      <>
        <Nav.Link as={Link} to="/" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('recommended') ? 'active-tab-box' : ''}`}><Home size={18} /> Home</Nav.Link>
        <Nav.Link as={Link} to="/roles" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('ongoing') ? 'active-link-box' : ''}`}><ChartBarDecreasingIcon size={18} /> Browse Roles</Nav.Link>
        <Nav.Link as={Link} to="/how-it-works" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('my-auditions') ? 'active-tab-box' : ''}`}><Workflow size={18} /> How It Works</Nav.Link>
        <Nav.Link as={Link} to="/dashboard/overview" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('overview') ? 'active-tab-box' : ''}`}><LayoutGrid size={18} /> Overview</Nav.Link>
      </>
    );

    return roleCode === '300' ? TalentLinks : DirectorLinks;
  };

  const renderPublicLinks = (isMobile = false) => {
    const baseClass = isMobile ? 'bottom-nav-item' : 'nav-item-custom';
    const activeClass = isMobile ? 'active-bottom-shadow' : 'active-tab-box';

    return (
      <>
        <Nav.Link as={Link} to="/" className={`${baseClass} ${(location.pathname.includes('HomePage') || location.pathname === '/') ? activeClass : ''}`}>
          <Home size={isMobile ? 22 : 18} /> {isMobile ? <span className="nav-label">Home</span> : "Home"}
        </Nav.Link>
        <Nav.Link as={Link} to="/roles" className={`${baseClass} ${location.pathname.includes('roles') ? activeClass : ''}`}>
          <PlayCircle size={isMobile ? 22 : 18} /> {isMobile ? <span className="nav-label">Browse Roles</span> : "Browse Roles"}
        </Nav.Link>
        <Nav.Link as={Link} to="/how-it-works" className={`${baseClass} ${location.pathname.includes('how-it-works') ? activeClass : ''}`}>
          <Workflow size={isMobile ? 22 : 18} /> {isMobile ? <span className="nav-label">How It Works</span> : "How It Works"}
        </Nav.Link>
      </>
    );
  };

  const renderBottomNavbar = () => {
    const clickHandler = handleClose; 
    if (!isMobile) return null;

    if (isAuthenticated) {
      return (
        <Navbar fixed="bottom" className="bottom-navbar">
          <Button variant="link" onClick={scrollLeft} className="scroll-button"><ChevronLeft size={24} color="white" /></Button>
          <Nav ref={navRef} className="bottom-nav-scrollable">
            {String(user?.roleCode) === '300' ? (
              <>
                <Nav.Link as={Link} to="/" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('recommended') ? 'active-tab-box' : ''}`}><Home size={18} /> <span className="nav-text">Home</span></Nav.Link>
                <Nav.Link as={Link} to="/roles" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('ongoing') ? 'active-tab-box' : ''}`}><PlayCircle size={18} /> <span className="nav-text">Browse Roles</span></Nav.Link>
                <Nav.Link as={Link} to="/how-it-works" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('how-it-works') ? 'active-tab-box' : ''}`}><Workflow size={18} /> <span className="nav-text">How It Works</span></Nav.Link>
                <Nav.Link as={Link} to="/dashboard/overview" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('overview') ? 'active-tab-box' : ''}`}><LayoutDashboard size={18} /> <span className="nav-text">Dashboard</span></Nav.Link>
                <Nav.Link as={Link} to="/dashboard/my-audition" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('my-audition') ? 'active-tab-box' : ''}`}><Briefcase size={18}/> <span className="nav-text">My application</span></Nav.Link>
                <Nav.Link as={Link} to="/dashboard/platform-feedback" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('platform-feedback') ? 'active-tab-box' : ''}`}><MessageSquare size={18} /> Platform Feedback</Nav.Link>

              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('recommended') ? 'active-tab-box' : ''}`}><Home size={18} /> Home</Nav.Link>
                <Nav.Link as={Link} to="/roles" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('ongoing') ? 'active-tab-box' : ''}`}><ChartBarDecreasingIcon size={18} /> Browse Roles</Nav.Link>
                <Nav.Link as={Link} to="/how-it-works" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('my-auditions') ? 'active-tab-box' : ''}`}><Workflow size={18} /> How It Works</Nav.Link>
                <Nav.Link as={Link} to="/dashboard/overview" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('overview') ? 'active-tab-box' : ''}`}><LayoutGrid size={18} /> Overview</Nav.Link>
                <Nav.Link as={Link} to="/dashboard/projects" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('projects') ? 'active-tab-box' : ''}`}><Briefcase size={18} /> Projects</Nav.Link>
                <Nav.Link as={Link} to="/dashboard/roles" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('roles') ? 'active-tab-box' : ''}`}><Users size={18} /> Manage Roles</Nav.Link>
                <Nav.Link as={Link} to="/dashboard/manage-applications" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('manage-applications') ? 'active-tab-box' : ''}`}><Users size={18} /> Audition</Nav.Link>
                <Nav.Link as={Link} to="/dashboard/transaction-history" onClick={clickHandler} className={`nav-item-bottom ${location.pathname.includes('transaction') ? 'active-tab-box' : ''}`}><CreditCard size={18} /> Billing</Nav.Link>
              </>
            )}
          </Nav>
          <Button variant="link" onClick={scrollRight} className="scroll-button"><ChevronRight size={24} color="white" /></Button>
        </Navbar>
      );
    } else {
      return (
        <Navbar fixed="bottom" className="bottom-navbar-container">
          <Container>
            <Nav className="w-100 d-flex justify-content-around align-items-center flex-row m-0 p-0">
              {renderPublicLinks(true)}
            </Nav>
          </Container>
        </Navbar>
      );
    }
  };

  return (
    <>
      <Navbar sticky="top" expand="lg" className="main-header">
        <Container fluid className="px-lg-5">
          
          <Navbar.Brand as={Link} to="/" className="brand-logo">
            <img src={logoBlackWhite} alt="BookMyAudition" />
          </Navbar.Brand>

          {/* --- DESKTOP NAVIGATION --- */}
          <div className="d-none d-lg-flex w-100 justify-content-between align-items-center">
            <Nav className="m-auto header-nav-links justify-content-center">
              {isAuthenticated ? renderDashboardLinks(false) : renderPublicLinks(false)}
            </Nav>

            <div className="d-flex align-items-center gap-2">
              
              {/* ✅ PWA DESKTOP INSTALL BUTTON */}
              {showInstallHeaderBtn && (
                <Button 
                  onClick={promptInstall}
                  variant="warning"
                  className="d-flex align-items-center gap-2 me-2 shadow-sm" 
                  style={{ fontWeight: 'bold', fontSize: '13px', borderRadius: '50px', backgroundColor: '#c30000', color: '#f9f9f9', border: 'none' }}
                >
                  <Download size={16} /> Install App
                </Button>
              )}

               {isAuthenticated && (
                  <div style={{ position: "relative" }}>
                    <NotificationBell count={notificationCount} onClick={() => setShowNotif(!showNotif)} />
                </div>
                )}

              <a href="tel:8795023799" className="header-contact-btn"><Phone size={16} /><span>Call</span></a>
              <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer" className="header-contact-btn"><FaWhatsapp size={16} /><span>WhatsApp</span></a>

            <div className="auth-section">
              {isAuthenticated ? (
                <Dropdown align="end">
                  <Dropdown.Toggle variant="link" className="profile-dropdown-btn p-0 no-caret"><UserCircle size={32} /></Dropdown.Toggle>
                  <Dropdown.Menu className="shadow-lg border-0 mt-2">
                    <Dropdown.Item as={Link} to="/dashboard/profile">My Profile</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/dashboard/ChangePasswordPage">Settings</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleLogout} className="text-danger">Sign Out</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <div className="d-flex gap-2">
                  <Link to="/login"><Button variant="outline-light" className="btn-login-pill">Login</Button></Link>
                  <Link to="/register"><Button className="btn-signup-pill">Sign Up</Button></Link>
                </div>
              )}
            </div>
          </div>
          </div> 
            
          {/* --- MOBILE NAVIGATION --- */}
          {isMobile ? (
            <div className="mobile-header-actions ms-auto d-flex align-items-center">

              {/* ✅ PWA MOBILE INSTALL BUTTON */}
              {showInstallHeaderBtn && (
                <button 
                  onClick={promptInstall} 
                  className="mobile-icon-btn me-2" 
                  style={{ backgroundColor: '#c30000', color: '#f9f9f9', border: 'none' }}
                >
                  <Download size={20} />
                </button>
              )}

              <a href="tel:8795023799" className="mobile-icon-btn"><Phone size={20} /></a>
              <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer" className="mobile-icon-btn"><FaWhatsapp size={20} /></a>

              {!isAuthenticated ? (
                <>
                  <div style={{ position: "relative" }}>
                    <NotificationBell count={notificationCount} onClick={() => setShowNotif(!showNotif)} />
                  </div>
                  <Link to="/login" className={`mobile-icon-btn ${isActive('/login') ? 'mobile-icon-active' : ''}`}><LogIn size={20} /></Link>
                  <Link to="/register" className={`mobile-icon-btn ${isActive('/register') ? 'mobile-icon-active' : ''}`}><UserPlus size={20} /></Link>
                </>
              ) : (
                <>
                  <Link to="/dashboard/profile" className={`mobile-icon-btn ${isActive('/dashboard/profile') ? 'mobile-icon-active' : ''}`}><UserCircle size={20} /></Link>
                  <button onClick={handleLogout} className="logout-icon-active"><LogOut size={20} /></button>
                </>
              )}

            </div>
          ) : (
            <button onClick={handleShow} className="d-lg-none border-0 bg-transparent p-2 ms-auto">
              <Menu size={28} color="#ffffff" />
            </button>
          )}

          <Offcanvas show={showDrawer} onHide={handleClose} placement="end" className="mobile-drawer">
            <Offcanvas.Header className="border-bottom" closeButton>
               <h5 className="mb-0 fw-bold brand-text text-white">BookMyAudition</h5>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-3 d-flex flex-column">
              {isAuthenticated && (
                <div className="mobile-user-info mb-4 d-flex align-items-center gap-3 p-3 bg-light-transparent rounded-3">
                  <UserCircle size={40} className="text-white" />
                  <div>
                    <h6 className="mb-0 fw-bold text-white">Welcome</h6>
                    <small className="text-white-50">Manage your account</small>
                  </div>
                </div>
              )}
              <Nav className="flex-column mobile-nav-links gap-2">
                 {isAuthenticated ? renderDashboardLinks(true) : renderPublicLinks(true)}
              </Nav>
              <hr className="my-4 border-white-50"/>
              <div className="mt-auto">
                {isAuthenticated ? (
                  <Nav className="flex-column gap-2">
                     <Nav.Link as={Link} to="/dashboard/profile" onClick={handleClose} className="nav-item-custom"><UserCircle size={18} /> My Profile</Nav.Link>
                     <Nav.Link as={Link} to="/dashboard/ChangePasswordPage" onClick={handleClose} className="nav-item-custom"><Settings size={18} /> Settings</Nav.Link>
                     <Button variant="outline-light" onClick={handleLogout} className="w-100 mt-3 d-flex align-items-center justify-content-center gap-2"><LogOut size={18} /> Sign Out</Button>
                  </Nav>
                ) : (
                  <div className="d-grid gap-3">
                    <Link to="/login" onClick={handleClose}><Button variant="outline-light" className="w-100">Login</Button></Link>
                    <Link to="/register" onClick={handleClose}><Button variant="light" className="w-100">Sign Up</Button></Link>
                  </div>
                )}
              </div>
            </Offcanvas.Body>
          </Offcanvas>

        </Container>
      </Navbar>

      {showNotif && (
        <div className="notif-dropdown">
          <h6 style={{ display: "flex", justifyContent: "space-between" }}>
            Notifications
            <span style={{ cursor: "pointer" }} onClick={() => setShowNotif(false)}>✖</span>
          </h6>
          {notifications.length === 0 ? (
            <p>No notifications</p>
          ) : (
            notifications.map((n, i) => (
              <div key={i} className="notif-item">
                <div style={{ fontWeight: "600" }}>{n.title}</div>
                <div style={{ fontSize: "12px", color: "#ccc" }}>{n.message}</div>
              </div>
            ))
          )}
        </div>
      )}
        
      {renderBottomNavbar()} 
      
    </>
  );
};

export default HeaderComponent;